/**
 * Light / Dark / System theme. The choice is per browser (localStorage `codot:theme`); "system" follows the OS and
 * keeps following it live. The resolved theme is `data-theme` on <html> — Tailwind's `dark:` variant keys off it
 * (style.css), and index.html applies it before the first paint so there is no flash.
 */
import { ref, watch } from 'vue';

export type ThemeChoice = 'system' | 'light' | 'dark';
const KEY = 'codot:theme';
const media = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;

function read(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system'; // storage blocked (private mode): follow the OS
  }
}

export const themeChoice = ref<ThemeChoice>(read());
export const resolvedTheme = ref<'light' | 'dark'>('light');

function apply(): void {
  resolvedTheme.value = themeChoice.value === 'system' ? (media?.matches ? 'dark' : 'light') : themeChoice.value;
  document.documentElement.dataset.theme = resolvedTheme.value;
}

watch(themeChoice, (c) => {
  try {
    if (c === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, c);
  } catch {
    /* not remembered; still applied for this visit */
  }
  apply();
});
media?.addEventListener('change', () => themeChoice.value === 'system' && apply());
// Another tab changed the theme → follow it.
window.addEventListener('storage', (e) => e.key === KEY && (themeChoice.value = read()));
apply();

export const THEME_OPTIONS: { value: ThemeChoice; label: string; icon: string }[] = [
  { value: 'light', label: 'Light', icon: 'M10 3v1.5M10 15.5V17M3 10h1.5M15.5 10H17M5.05 5.05l1.06 1.06M13.89 13.89l1.06 1.06M5.05 14.95l1.06-1.06M13.89 6.11l1.06-1.06M13 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0z' },
  { value: 'system', label: 'System', icon: 'M3.5 5h13v8.5h-13zM7.5 16.5h5M10 13.5v3' },
  { value: 'dark', label: 'Dark', icon: 'M15.5 12.2A6 6 0 0 1 7.8 4.5a6 6 0 1 0 7.7 7.7z' },
];
